// Indexes the raw tracker rows by date. The reserved lookup rows (README §4)
// are matched by name, as the backend itself does.

const RESERVED = {
  noSymptoms: 'no symptoms',
  noCravings: 'no cravings',
  flowNotSpecified: 'not specified',
};

const lower = (s) => String(s || '').trim().toLowerCase();

/**
 * @returns {Record<string, { flow: string|null, flowNotRecorded: boolean, spotting: boolean, pain: number|null }>}
 */
export function indexPeriodLogs(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    if (r.is_deleted) return;
    const flowName = r.period_flow || null;
    const notSpecified = lower(flowName) === RESERVED.flowNotSpecified;
    out[r.date] = {
      flow: flowName && !notSpecified ? flowName : null,
      flowNotRecorded: notSpecified,
      spotting: Boolean(r.spotting),
      pain: r.period_pain ?? null,
    };
  });
  return out;
}

/**
 * Multi-item trackers (symptoms, cravings) → date → `'none'` for a confirmed
 * zero day, or `{ [name]: value }` for the items rated that day.
 */
function indexMultiLogs(rows, nameKey, valueKey, zeroName) {
  const out = {};
  (rows || []).forEach((r) => {
    if (r.is_deleted) return;
    const name = r[nameKey];
    if (lower(name) === zeroName) {
      if (out[r.date] == null) out[r.date] = 'none';
      return;
    }
    const day = out[r.date] && out[r.date] !== 'none' ? out[r.date] : {};
    day[name] = r[valueKey];
    out[r.date] = day;
  });
  return out;
}

export const indexSymptomLogs = (rows) =>
  indexMultiLogs(rows, 'symptom_name', 'symptom_score', RESERVED.noSymptoms);

export const indexCravingLogs = (rows) =>
  indexMultiLogs(rows, 'craving_category_name', 'craving_intensity', RESERVED.noCravings);

/** date → energy rating (0 = "Drained", a real rating). */
export function indexEnergyLogs(rows) {
  const out = {};
  (rows || []).forEach((r) => {
    if (!r.is_deleted && r.energy_ratings != null) out[r.date] = r.energy_ratings;
  });
  return out;
}

/**
 * One day of a multi-item tracker, aggregated.
 * @returns {null | { none: boolean, list: Array<{name: string, val: number}>, sum: number, n: number, avg: number }}
 */
export function dayAggregate(value) {
  if (value == null) return null;
  if (value === 'none') return { none: true, list: [], sum: 0, n: 0, avg: 0 };
  const list = Object.entries(value).map(([name, val]) => ({ name, val }));
  const sum = list.reduce((a, b) => a + b.val, 0);
  return { none: false, list, sum, n: list.length, avg: list.length ? sum / list.length : 0 };
}
